/**
 * Typed fetch wrapper (frontend-map §3.1). The ONE place the app talks to the API.
 * Components never call `fetch` directly — they go through feature hooks that use this.
 *
 * - Paths are `/api/...`; Vite proxies `/api` → Fastify in dev, same-origin in prod.
 *   No base URL, no hardcoded port.
 * - Auth headers come from the active auth adapter (`@/auth`): clerk attaches the
 *   session `Authorization: Bearer <token>`; the dev bypass attaches its persona
 *   header. The client itself is auth-agnostic. Every feature request is
 *   authenticated (only `/api/health` is public, never called for features).
 * - On non-2xx, parses the standard error envelope and throws a typed `ApiError`.
 *   Callers key off `error.code`, never the message text.
 * - On 204, resolves `undefined`.
 */
import { authAdapter } from '@/auth';
import { clientId } from '@/lib/clientId';
import type { ErrorCode, ErrorResponse } from '@ultimate-checklist/shared';

export class ApiError extends Error {
  readonly code: ErrorCode | 'NETWORK_ERROR';
  readonly status: number;
  readonly details: unknown;

  constructor(
    code: ErrorCode | 'NETWORK_ERROR',
    message: string,
    status: number,
    details: unknown = null,
  ) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'DELETE';

export interface FetchOptions {
  method?: HttpMethod;
  body?: unknown;
  signal?: AbortSignal;
}

function isErrorResponse(value: unknown): value is ErrorResponse {
  return (
    typeof value === 'object' &&
    value !== null &&
    'error' in value &&
    typeof (value as { error: unknown }).error === 'object'
  );
}

export async function fetchApi<T>(path: string, opts: FetchOptions = {}): Promise<T> {
  const { method = 'GET', body, signal } = opts;

  // Per-tab id: the live stream echoes it so a tab can tell its own edits apart.
  const headers: Record<string, string> = { 'x-client-id': clientId };
  await authAdapter.decorateRequest(headers);

  let serializedBody: string | undefined;
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
    serializedBody = JSON.stringify(body);
  }

  let response: Response;
  try {
    response = await fetch(path, { method, headers, body: serializedBody, signal });
  } catch (err) {
    throw new ApiError('NETWORK_ERROR', 'Network request failed', 0, err);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  if (!response.ok) {
    let payload: unknown = null;
    try {
      payload = await response.json();
    } catch {
      // Non-JSON error body; fall through to a generic message.
    }
    if (isErrorResponse(payload)) {
      throw new ApiError(
        payload.error.code,
        payload.error.message,
        response.status,
        payload.error.details ?? null,
      );
    }
    throw new ApiError(
      'INTERNAL',
      `Request failed with status ${response.status}`,
      response.status,
    );
  }

  return (await response.json()) as T;
}
